import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FeatureCollection, Point as TPoint } from 'geojson';
import { Subscription, debounceTime, distinctUntilChanged, skip, tap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { IEventsMapTiles, Language } from '../../models';
import { EventsBrowseService } from '../events-browse.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { EventsMapCarouselComponent } from './events-map-carousel/events-map-carousel.component';

// leaflet
let L;

@Component({
  selector: 'app-events-map',
  templateUrl: './events-map.component.html',
  styleUrls: ['./events-map.component.scss'],
  standalone: true,
  imports: [LoaderComponent, EventsMapCarouselComponent],
})
export class EventsMapComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private eventsBrowseService = inject(EventsBrowseService);
  private translocoService = inject(TranslocoService);
  private platform = inject(Platform);

  private isBrowser = false;
  public isLoading = false;

  public selectedMarker: string;
  public selectedEvent: number[];

  public eventsGeoJSON: FeatureCollection<TPoint, {
    dateEnd?: Date,
    dateStart: Date,
    imgUrl: string,
    name: string,
    organizerName: string,
    url: string,
    oid?: number,
    showTime?: boolean,
  }>;
  // eslint-disable-next-line no-undef
  private map: L.Map;
  private markersCluster;
  private language = Language.frFR;
  private eventsMapTiles: IEventsMapTiles;

  private translationSub: Subscription;
  private formSub: Subscription;

  constructor() {
    this.isBrowser = this.platform.isBrowser;
  }

  ngOnInit(): void {
    this.eventsBrowseService.browseForm.controls.sort?.disable({emitEvent: false, onlySelf: true});
    this.eventsBrowseService.browseForm.controls.displayStyle?.setValue('map', {emitEvent: false, onlySelf: true});

    // set leaflet variable
    L = window.L; // todo show error message if L is undefined
    this.translationSub = this.translocoService.langChanges$
      .subscribe((language: Language) => {
        this.language = language;
      });
    // get event from service
    this.eventsGeoJSON = this.eventsBrowseService.eventsGeoJSON?.eventsGeoJSON;
    this.eventsMapTiles = this.eventsBrowseService.eventsGeoJSON?.eventsMapTiles;

    // redirect if map is disabled
    if (!this.eventsGeoJSON) {
      this.router.navigate(['..'], { relativeTo: this.route });
      return;
    }

    // add properties to events object
    const eventSession = {};
    this.eventsGeoJSON.features.forEach( (f) => {
      const sessionId = (f.properties.name as string).toLowerCase() + new Date(f.properties.dateStart)?.getDate();
      eventSession[sessionId] = eventSession[sessionId] ? eventSession[sessionId] + 1 : 1;
      return f;
    });
    this.eventsGeoJSON.features = this.eventsGeoJSON.features.map( (f, i) => {
      f.properties.oid = i;
      const sessionId = (f.properties.name as string).toLowerCase() + new Date(f.properties.dateStart)?.getDate();
      if (eventSession[sessionId] > 1) {
        f.properties.showTime = true;
      }
      return f;
    });
  }

  ngAfterViewInit(): void {
    if (!this.eventsGeoJSON) {
      return;
    }
    // map setup
    const eventFiltered = structuredClone(this.eventsGeoJSON);
    eventFiltered.features = eventFiltered.features.filter( (f) => {
      const eventName = f.properties.name as string;
      return eventName?.toLowerCase().includes(this.eventsBrowseService.browseModel?.searchQuery ?? '');
    });
    this.initMap();
    this.makeEventMarkers(this.map, eventFiltered);

    // subscribe to search changes
    this.formSub = this.eventsBrowseService.browseForm.valueChanges.pipe(
      skip(1),
      distinctUntilChanged(),
      tap( () => {
        this.isLoading = true;
      }),
      debounceTime(100),
    ).subscribe( (search) => {
      if (this.map) {
        this.markersCluster.clearLayers();
        const eventFiltered = structuredClone(this.eventsGeoJSON);
        eventFiltered.features = eventFiltered.features.filter( (f) => {
          const eventName = f.properties.name as string;
          return eventName?.toLowerCase().includes(search?.searchQuery?.toLowerCase());
        });
        this.makeEventMarkers(this.map, eventFiltered);
        this.isLoading = false;
      }
    });
  }

  private initMap(): void {
    this.map = L.map('map', {
      center: [ 46.7667, 2.45 ], // centre géographique de la france
      zoom: 6, // zoom minimum pour la carte en breton
    });
    // localisation de l'utilisateur
    (L.control as any).locate({
      flyTo: true,
      initialZoomLevel: 10,
    }).addTo(this.map);

    const tiles = L.tileLayer(this.eventsMapTiles.url, this.eventsMapTiles.options);

    tiles.addTo(this.map);
  }

  // eslint-disable-next-line no-undef
  private makeEventMarkers(map: L.Map, geoJSONFeatures: FeatureCollection<TPoint>): void {
    this.markersCluster = L.markerClusterGroup({
      spiderfyOnMaxZoom: false,
      singleMarkerMode: true,
      maxClusterRadius: 30,
      iconCreateFunction(cluster) {
        const size = 2 +  Math.min(Math.floor((cluster.getChildCount() / 3)) , 4);
        const html = '<div><span><b>' + cluster.getChildCount() + '</b></span></div>';
        let active = false;
        const markerEventsId = cluster.getAllChildMarkers()?.map( (m) => m.options?.oid)?.join('-');
        if (this.isBrowser && (window as any).selectedMarker && (window as any).selectedMarker === markerEventsId) {
          active = true;
        }
        return L.divIcon({
          html,
          className: `map-cluster --s${size} --id${markerEventsId} ${active ? '--active' : ''}`,
          iconSize: L.point((1+size/6) * 30, (1+size/6) * 30, true),
        });
      },
    });
    const blackIcon = new L.Icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-black.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [10, -32],
      shadowSize: [41, 41],
    });
    for (let i = 0; i < geoJSONFeatures.features.length; i++) {
      const c = geoJSONFeatures.features?.[i];
      const lon = c.geometry.coordinates[0];
      const lat = c.geometry.coordinates[1];
      const marker = L.marker([lat, lon], {
        alt: c.properties?.name,
        icon: blackIcon,
        oid: c.properties?.oid,
      });

      marker.eventUrl = c.properties?.url;
      marker.index = i;
      this.markersCluster.addLayer(marker);
    }
    map.addLayer(this.markersCluster);

    // on single event click => set active & update events carousel
    this.markersCluster.on('click', (a) => {
      this.selectedEvent = [a.layer?.options?.oid];
      // eslint-disable-next-line no-underscore-dangle
      const selectedCluster = a.layer._icon;
      (selectedCluster.classList as DOMTokenList).forEach( (cl) => {
        if (this.isBrowser && cl && cl.startsWith('--id')) {
          this.selectedMarker = cl.slice(4);
          (window as any).selectedMarker = this.selectedMarker;
        }
      });
      // Ensure Clusters icons are redrawn
      this.markersCluster?.refreshClusters();
    });
    // on cluster click => set active & update events carousel
    this.markersCluster.on('clusterclick', (a) => {
      // a.layer is actually a cluster
      const markers = a.layer.getAllChildMarkers();
      this.selectedEvent = markers.map( (m) => m.options?.oid);
      // eslint-disable-next-line no-underscore-dangle
      const selectedCluster = a.layer._icon;
      (selectedCluster.classList as DOMTokenList).forEach( (cl) => {
        if (this.isBrowser && cl && cl.startsWith('--id')) {
          this.selectedMarker = cl.slice(4);
          (window as any).selectedMarker = this.selectedMarker;
        }
      });
      // Ensure Clusters icons are redrawn
      this.markersCluster.refreshClusters();
    });
  }

  ngOnDestroy(): void {
    this.translationSub?.unsubscribe();
    this.formSub?.unsubscribe();
  }
}
